import { getGlobalHeadersAsObject } from "./global-headers";
import type { UserProfile } from "./types";

/**
 * ConversationSyncStatus - computed fields from dedicated sync-status endpoint.
 */
export interface ConversationSyncStatus {
	synced_conversations: number;
	/** Inbox jobs still owed — message fetches and list pages. See
	 * ConnectionSyncStatus.pending_sync_jobs for why it gates everything else. */
	pending_sync_jobs: number;
	currently_syncing: boolean;
	conversations_per_second: number | null;
}

/**
 * Get current user's profile (generic - returns ALL UserProfile fields).
 * DRF returns a list with single item - we extract it.
 */
export const getCurrentUserProfile = async (): Promise<UserProfile> => {
	const response = await fetch(`/api/v2/current-userprofile/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	});
	const data = await response.json();
	// DRF list endpoint returns array, extract first (only) item
	return data.results?.[0] || data[0];
};

/**
 * Get conversation sync status with computed fields.
 * Uses dedicated endpoint for performance (doesn't load full UserProfile).
 */
export const getConversationSyncStatus = (): Promise<ConversationSyncStatus> => {
	return fetch(`/api/v2/conversations-sync/sync-status/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

/**
 * Advisory recommended daily email cap, derived from the user's send history.
 */
export interface CeilingStep {
	label: string;
	detail: string;
	value: string;
}
export interface EmailSendRecommendation {
	recommended: number; // suggested hard cap (steady-state safe ceiling)
	safe_ceiling_now: number; // what the spike-guard enforces right now
	safe_ceiling_steps: CeilingStep[]; // how safe_ceiling_now was calculated
	safe_peak: number;
	avg_per_active_day: number;
	median_per_active_day: number;
	account_age_since: string;
}

export const getRecommendedEmailCap = (
	emailMethod?: number
): Promise<EmailSendRecommendation> => {
	const qs = emailMethod != null ? `?email_method=${emailMethod}` : "";
	return fetch(`/api/v2/current-userprofile/recommended_cap/${qs}`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

/**
 * All currently-paused senders, for the app-wide banner. Populated only for
 * admin/staff viewers; empty otherwise.
 */
export interface SuspendedUser {
	user_id: number;
	email: string;
	until: string;
	reason: string | null;
}
export interface SendingSuspensions {
	paused: SuspendedUser[];
}

export const getSendingSuspensions = (): Promise<SendingSuspensions> => {
	return fetch(`/api/v2/current-userprofile/sending_suspensions/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

/**
 * Whether the viewer's own LinkedIn plugin is working, for the app-wide banner.
 * Only problems the user can fix themselves are reported: the plugin is not
 * running, its ODI login has expired, its browser is signed out of LinkedIn, or
 * its LinkedIn session is missing a cookie every LinkedIn request needs.
 * `last_seen` is when it last got through to ODI — null if it never has.
 */
export interface PluginHealth {
	problem:
		| "not_running"
		| "odi_signed_out"
		| "linkedin_signed_out"
		| "linkedin_session_problem"
		| null;
	last_seen: string | null;
	/** Where the user's most recent plugin runs: the Artemis Windows app, or the
	 * Artemis Helper extension in a browser. Null if they never ran one. */
	host: "artemis" | "browser" | null;
	/** The browser's name ("Edge") when host is "browser". */
	browser: string | null;
	/** The Artemis app is too old to start itself at Windows login. */
	update_needed: boolean;
}

export const getPluginHealth = (): Promise<PluginHealth> => {
	return fetch(`/api/v2/current-userprofile/plugin_health/`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

/**
 * Why this sender's LinkedIn invites are (or are not) going out right now.
 * `resumes_at` is null when nothing blocks sending, and when a weekly cap of 0
 * means it never reopens on its own.
 */
export type InviteCapacityState =
	| "no_bot"
	| "ok"
	| "cooldown_429"
	| "weekly_budget_exhausted"
	/** Plugin spent its hourly LinkedIn call ceiling on other work. The weekly
	 * budget is untouched, so `capacity` still reports the real headroom. */
	| "plugin_hourly_budget_spent";

export interface InviteCapacityStatus {
	state: InviteCapacityState;
	capacity: number;
	max_weekly: number;
	used_7d: number;
	resumes_at: string | null;
}

export const getInviteCapacityStatus = (
	userId?: number
): Promise<InviteCapacityStatus> => {
	const qs = userId != null ? `?user_id=${userId}` : "";
	return fetch(`/api/v2/current-userprofile/invite_capacity/${qs}`, {
		method: "GET",
		headers: getGlobalHeadersAsObject(),
	}).then((res) => res.json());
};

/**
 * Update current user's profile (generic - can update ANY UserProfile field).
 * Works for both connection sync fields and conversation sync fields.
 */
export const updateUserProfile = (
	id: number,
	updates: Partial<UserProfile>
): Promise<UserProfile> => {
	return fetch(`/api/v2/current-userprofile/${id}/`, {
		method: "PATCH",
		headers: {
			...getGlobalHeadersAsObject(),
			"Content-Type": "application/json",
		},
		body: JSON.stringify(updates),
	}).then((res) => res.json());
};
